import React, { useState } from 'react';
import { Card, Button, Input, Space, Alert, Typography, Tag, Spin, message } from 'antd';
import { RobotOutlined, SendOutlined } from '@ant-design/icons';
import { analyzeHealthData, getHealthAdvice } from '@/services/gemini';
import { useHealthStore } from '@/stores/useHealthStore';
import type { AIAnalysisResult } from '@/types';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const AIAssistant: React.FC = () => {
  const { bpRecords, bsRecords } = useHealthStore();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [analysis, setAnalysis] = useState<AIAnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'analysis' | 'chat'>('analysis');

  const handleAnalyze = async () => {
    if (bpRecords.length === 0 && bsRecords.length === 0) {
      message.warning('请先录入一些健康数据');
      return;
    }

    setLoading(true);
    try {
      const result = await analyzeHealthData(bpRecords, bsRecords);
      setAnalysis(result);
      setActiveTab('analysis');
    } catch (error) {
      message.error('分析失败，请检查API配置');
    } finally {
      setLoading(false);
    }
  };

  const handleAsk = async () => {
    if (!question.trim()) {
      message.warning('请输入问题');
      return;
    }

    setLoading(true);
    try {
      const result = await getHealthAdvice(question, bpRecords, bsRecords);
      setAnswer(result);
      setActiveTab('chat');
    } catch (error) {
      message.error('获取建议失败，请检查API配置');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 24 }}>
        <RobotOutlined style={{ marginRight: 8 }} />
        AI健康助手
      </h1>

      <Alert
        message="AI辅助健康分析"
        description="基于您的历史数据，AI可以提供趋势分析和健康建议。请注意：AI建议仅供参考，不能替代专业医生的诊断。"
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Space style={{ marginBottom: 24 }}>
        <Button
          type={activeTab === 'analysis' ? 'primary' : 'default'}
          onClick={() => setActiveTab('analysis')}
        >
          智能分析
        </Button>
        <Button
          type={activeTab === 'chat' ? 'primary' : 'default'}
          onClick={() => setActiveTab('chat')}
        >
          健康咨询
        </Button>
      </Space>

      {activeTab === 'analysis' && (
        <Card title="数据趋势分析">
          <Button
            type="primary"
            onClick={handleAnalyze}
            loading={loading}
            style={{ marginBottom: 16 }}
          >
            分析我的健康数据
          </Button>

          {loading && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <p style={{ marginTop: 16 }}>AI正在分析您的健康数据...</p>
            </div>
          )}

          {analysis && !loading && (
            <div>
              <Title level={4}>分析摘要</Title>
              <Paragraph>{analysis.summary}</Paragraph>

              {analysis.trends.length > 0 && (
                <>
                  <Title level={5}>趋势变化</Title>
                  <ul>
                    {analysis.trends.map((trend, i) => (
                      <li key={i}><Text>{trend}</Text></li>
                    ))}
                  </ul>
                </>
              )}

              {analysis.anomalies.length > 0 && (
                <>
                  <Title level={5}>异常提醒</Title>
                  {analysis.anomalies.map((anomaly, i) => (
                    <Tag key={i} color="red" style={{ margin: 4 }}>{anomaly}</Tag>
                  ))}
                </>
              )}

              {analysis.suggestions.length > 0 && (
                <>
                  <Title level={5}>健康建议</Title>
                  <ul>
                    {analysis.suggestions.map((suggestion, i) => (
                      <li key={i}><Text>{suggestion}</Text></li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </Card>
      )}

      {activeTab === 'chat' && (
        <Card title="健康咨询">
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <TextArea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="请输入您的问题，例如：最近我的血压怎么样？有什么建议？"
              rows={3}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleAsk}
              loading={loading}
            >
              询问AI
            </Button>

            {answer && !loading && (
              <Card type="inner" title="AI回答">
                <Paragraph>{answer}</Paragraph>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  免责声明：以上建议仅供参考，不能替代专业医生的诊断和治疗建议。
                </Text>
              </Card>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default AIAssistant;
